import { createContext, useState, ReactNode, useEffect } from 'react';
import { PaletteMode, Direction } from '@mui/material';
import themeConfig from 'src/configs/themeConfig';
import { Skin, AppBar, Footer, ThemeColor, ContentWidth, VerticalNavToggle } from 'src/@core/layouts/types';

export type Settings = {
  skin: Skin;
  appBar?: AppBar;
  footer?: Footer;
  mode: PaletteMode;
  navHidden?: boolean; // navigation menu
  appBarBlur: boolean;
  appShowDeleted: boolean;
  isImpersonating?: boolean;
  developerMode: boolean;
  direction: Direction;
  navCollapsed: boolean;
  themeColor: ThemeColor;
  contentWidth: ContentWidth;
  layout?: 'vertical' | 'horizontal';
  lastLayout?: 'vertical' | 'horizontal';
  verticalNavToggleType: VerticalNavToggle;
  toastPosition?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  unlockDashboardLayout: boolean;
  compactType: 'vertical' | 'horizontal' | 'none';
  preventCollisions: boolean;
  allowOverlap: boolean;
  currentRole: string;
  hideOrderConfirmationModal: boolean;
  showUATRecords: boolean;
};

export type PageSpecificSettings = Partial<Settings>;

export type SettingsContextValue = {
  settings: Settings;
  saveSettings: (updatedSettings: Settings) => void;
};

interface SettingsProviderProps {
  children: ReactNode;
  pageSettings?: PageSpecificSettings | void;
}

const STATIC_KEYS: (keyof Settings)[] = ['appBar', 'footer', 'layout', 'lastLayout', 'navHidden', 'toastPosition'];

const initialSettings: Settings = {
  themeColor: 'primary',
  mode: themeConfig.mode,
  footer: themeConfig.footer,
  layout: themeConfig.layout,
  lastLayout: themeConfig.layout,
  direction: themeConfig.direction,
  navHidden: themeConfig.navHidden,
  appBarBlur: themeConfig.appBarBlur,
  appShowDeleted: themeConfig.appShowDeleted,
  developerMode: themeConfig.developerMode,
  navCollapsed: themeConfig.navCollapsed,
  contentWidth: themeConfig.contentWidth,
  toastPosition: themeConfig.toastPosition,
  verticalNavToggleType: themeConfig.verticalNavToggleType,
  skin: themeConfig.layout === 'horizontal' && themeConfig.skin === 'semi-dark' ? 'default' : themeConfig.skin,
  appBar: themeConfig.layout === 'horizontal' && themeConfig.appBar === 'hidden' ? 'fixed' : themeConfig.appBar,
  unlockDashboardLayout: themeConfig.unlockDashboardLayout,
  compactType: themeConfig.compactType,
  preventCollisions: themeConfig.preventCollisions,
  allowOverlap: themeConfig.allowOverlap,
  showUATRecords: false,
  currentRole: '',
  hideOrderConfirmationModal: false,
  isImpersonating: false
};

const normalizeSettings = (settings: Settings): Settings => {
  const adjusted = { ...settings };

  if (adjusted.layout === 'horizontal') {
    if (adjusted.skin === 'semi-dark') adjusted.skin = 'default';
    if (adjusted.appBar === 'hidden') adjusted.appBar = 'fixed';
  }

  return adjusted;
};

const restoreSettings = (): Settings => {
  try {
    const stored = localStorage.getItem('settings');
    if (!stored) return initialSettings;

    const parsed = JSON.parse(stored);
    const staticProps = Object.fromEntries(STATIC_KEYS.map(key => [key, initialSettings[key]]));

    return normalizeSettings({ ...parsed, ...staticProps });
  } catch {
    return initialSettings;
  }
};

const storeSettings = (settings: Settings) => {
  const toStore = { ...settings };
  STATIC_KEYS.forEach(key => delete toStore[key]);
  localStorage.setItem('settings', JSON.stringify(toStore));
};

export const SettingsContext = createContext<SettingsContextValue>({
  settings: initialSettings,
  saveSettings: () => null
});

export const SettingsProvider = ({ children, pageSettings }: SettingsProviderProps) => {
  const [settings, setSettings] = useState<Settings>({ ...initialSettings });

  useEffect(() => {
    const restored = restoreSettings();
    const merged = normalizeSettings({ ...restored, ...pageSettings });
    setSettings(merged);
  }, [pageSettings]);

  const saveSettings = (updated: Settings) => {
    const normalized = normalizeSettings(updated);
    storeSettings(normalized);
    setSettings(normalized);
  };

  return <SettingsContext.Provider value={{ settings, saveSettings }}>{children}</SettingsContext.Provider>;
};

export const SettingsConsumer = SettingsContext.Consumer;
